// Created: 2025-01-27
// Modified: 2025-01-27 15:45 KST - Add remark-gfm for tables and anchor link support
// Modified: 2025-12-19 13:32 KST - Read manual type from URL query parameter
// Modified: 2025-12-19 13:35 KST - Remove duplicate accordion menu from page (moved to sidebar)
// src/pages/manual/Manual.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import '@assets/css/common/style_main.css';

function Manual() {
  const [searchParams] = useSearchParams();
  const [markdownContent, setMarkdownContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [selectedManual, setSelectedManual] = useState(() => {
    // URL 쿼리 파라미터에서 매뉴얼 타입 읽기
    const type = searchParams.get('type') || 'product';
    return type === 'admin' ? 'admin' : 'product';
  });
  const contentRef = useRef(null);
  
  // Modified: 2025-01-27 [현재 시간 추가 필요] - Add manual file mapping with paths
  // 매뉴얼 파일 매핑
  const manualFiles = {
    product: {
      file: 'Postme 제품 매뉴얼 v1.0.2.md',
      path: 'product',
      resourcesPath: 'product/resources'
    },
    admin: {
      file: 'Postme admin user Manual v1.0.1.md',
      path: 'admin',
      resourcesPath: 'admin/resources' // admin 매뉴얼은 admin의 resources를 참조
    }
  };
  
  const currentManual = manualFiles[selectedManual] || manualFiles.product;
  const manualFile = currentManual.file;
  const manualPath = currentManual.path;
  const resourcesPath = currentManual.resourcesPath;

  // URL 쿼리 파라미터 변경 감지
  useEffect(() => {
    const type = searchParams.get('type') || 'product';
    const newType = type === 'admin' ? 'admin' : 'product';
    if (newType !== selectedManual) {
      setSelectedManual(newType);
      setLoading(true);
      setError(null);
    }
  }, [searchParams, selectedManual]);

  useEffect(() => {
    // Modified: 2025-01-27 [현재 시간 추가 필요] - Load styles.css from product folder (shared)
    // styles.css 로드 (product와 admin이 공유)
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = '/manual/product/styles.css';
    document.head.appendChild(link);

    // Modified: 2025-01-27 [현재 시간 추가 필요] - Load markdown file from correct path based on manual type
    // public 폴더의 마크다운 파일 로드
    fetch(`/manual/${manualPath}/${manualFile}`)
      .then((response) => {
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('매뉴얼 파일을 찾을 수 없습니다. 파일이 준비되면 추가될 예정입니다.');
          }
          throw new Error('마크다운 파일을 불러올 수 없습니다.');
        }
        return response.text();
      })
      .then((text) => {
        // Modified: 2025-12-19 14:22:23 - Convert image paths based on manual type
        // 이미지 경로를 절대 경로로 변환
        // HTML img 태그의 src 속성 변환
        let processedText = text.replace(
          /src="\.\.\/product\/resources\//g,
          `src="/manual/${resourcesPath}/`
        );
        processedText = processedText.replace(
          /src="\.\/resources\//g,
          `src="/manual/${resourcesPath}/`
        );
        // resources/로 시작하는 경로 처리 (admin 매뉴얼용)
        processedText = processedText.replace(
          /src="resources\//g,
          `src="/manual/${manualPath}/resources/`
        );
        // 마크다운 이미지 문법도 절대 경로로 변환 (![alt](./resources/image.png))
        processedText = processedText.replace(
          /!\[([^\]]*)\]\(\.\.\/product\/resources\//g,
          `![$1](/manual/${resourcesPath}/`
        );
        processedText = processedText.replace(
          /!\[([^\]]*)\]\(\.\/resources\//g,
          `![$1](/manual/${resourcesPath}/`
        );
        // resources/로 시작하는 마크다운 이미지 경로 처리 (admin 매뉴얼용)
        processedText = processedText.replace(
          /!\[([^\]]*)\]\(resources\//g,
          `![$1](/manual/${manualPath}/resources/`
        );
        setMarkdownContent(processedText);
        setLoading(false);
        setError(null);
      })
      .catch((err) => {
        console.error('Error loading markdown:', err);
        setError(err.message);
        setMarkdownContent('');
        setLoading(false);
      });

    // cleanup: 컴포넌트 언마운트 시 link 제거
    return () => {
      const existingLink = document.querySelector('link[href="/manual/product/styles.css"]');
      if (existingLink) {
        document.head.removeChild(existingLink);
      }
    };
  }, [manualFile, manualPath, resourcesPath]);

  // Modified: 2025-01-27 [현재 시간 추가 필요] - Update PDF download to use correct path based on manual type
  // Modified: 2025-01-27 - Admin 매뉴얼 PDF 다운로드 경로 확인 및 수정
  // PDF 파일 다운로드 함수
  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      // PDF 파일명 생성: 마크다운 파일명에서 .md를 .pdf로 변경
      // admin: Postme admin user Manual v1.0.1.md -> Postme admin user Manual v1.0.1.pdf
      // product: Postme 제품 매뉴얼 v1.0.2.md -> Postme 제품 매뉴얼 v1.0.2.pdf
      const pdfFiles = [
        `${manualFile.replace('.md', '.pdf')}`,
        // 추가 PDF 파일이 있다면 여기에 추가
      ];

      let latestFile = null;
      let latestDate = null;

      // 각 PDF 파일의 수정 날짜 확인
      // admin 매뉴얼의 경우 /manual/admin 폴더에서 PDF 파일 찾기
      // product 매뉴얼의 경우 /manual/product/resources 폴더에서 PDF 파일 찾기
      const pdfBasePath = selectedManual === 'admin' ? `/manual/admin` : `/manual/${resourcesPath}`;
      
      for (const fileName of pdfFiles) {
        try {
          const response = await fetch(`${pdfBasePath}/${encodeURIComponent(fileName)}`, {
            method: 'HEAD'
          });
          
          if (response.ok) {
            const lastModified = response.headers.get('Last-Modified');
            if (lastModified) {
              const fileDate = new Date(lastModified);
              if (!latestDate || fileDate > latestDate) {
                latestDate = fileDate;
                latestFile = fileName;
              }
            } else {
              // Last-Modified 헤더가 없으면 파일명으로 판단 (첫 번째 파일 우선)
              if (!latestFile) {
                latestFile = fileName;
              }
            }
          }
        } catch (err) {
          console.warn(`파일 확인 실패: ${fileName}`, err);
        }
      }

      if (!latestFile) {
        // 파일을 찾지 못한 경우 기본 파일명 사용
        latestFile = pdfFiles[0];
      }

      // 파일 다운로드
      const fileUrl = `${pdfBasePath}/${encodeURIComponent(latestFile)}`;
      const response = await fetch(fileUrl);
      
      if (!response.ok) {
        throw new Error('PDF 파일을 다운로드할 수 없습니다.');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = latestFile;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('PDF 다운로드 실패:', err);
      alert('PDF 파일을 다운로드하는 중 오류가 발생했습니다.');
    } finally {
      setDownloading(false);
    }
  };

  // 스크롤을 특정 요소로 이동하는 헬퍼 함수
  const scrollToElement = (elementId) => {
    if (!elementId) return;
    
    // URL 디코딩 처리
    const decodedId = decodeURIComponent(elementId);
    
    // 약간의 지연을 두고 스크롤 (DOM 업데이트 대기)
    setTimeout(() => {
      const targetElement = document.getElementById(decodedId);
      if (!targetElement) {
        console.warn(`Element with id "${decodedId}" not found`);
        return;
      }
      
      // 스크롤 컨테이너 확인
      const scrollContainer = contentRef.current;
      if (scrollContainer) {
        // getBoundingClientRect를 사용한 정확한 위치 계산
        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRect = targetElement.getBoundingClientRect();
        
        // 현재 스크롤 위치 + 요소의 상대 위치 - 컨테이너의 상대 위치
        const scrollTop = scrollContainer.scrollTop + (elementRect.top - containerRect.top) - 20;
        
        scrollContainer.scrollTo({
          top: Math.max(0, scrollTop),
          behavior: 'smooth'
        });
      } else {
        // 일반 스크롤
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300);
  };

  // 마크다운 렌더링 후 제목에 ID 추가 및 앵커 링크 처리
  useEffect(() => {
    if (!contentRef.current || loading) return;

    const content = contentRef.current;
    
    // 모든 제목(h1-h6)에 ID 추가
    const headings = content.querySelectorAll('h1, h2, h3, h4, h5, h6');
    const headingIds = [];
    headings.forEach((heading) => {
      // 항상 ID를 재생성 (기존 ID가 있어도)
      let text = heading.textContent || '';
      
      // 앞의 번호와 점 제거 (예: "1. 문서 개요" -> "문서 개요")
      const numberMatch = text.match(/^(\d+)\.\s*(.+)$/);
      let number = null;
      let titleText = text;
      
      if (numberMatch) {
        number = numberMatch[1];
        titleText = numberMatch[2].trim();
      }
      
      // GitHub Flavored Markdown 앵커 링크 형식으로 ID 생성
      // 소문자 변환, 공백을 하이픈으로, 특수문자 제거
      let id = titleText
        .toLowerCase()
        .replace(/[^\w\s-가-힣]/g, '') // 특수문자 제거 (점, 괄호 등)
        .replace(/\s+/g, '-') // 공백을 하이픈으로
        .replace(/-+/g, '-') // 연속된 하이픈 제거
        .replace(/^-|-$/g, '') // 앞뒤 하이픈 제거
        .trim();
      
      // 번호가 있는 경우 앞에 추가 (예: "1-문서-개요")
      if (number) {
        heading.id = `${number}-${id}`;
      } else {
        heading.id = id || 'heading';
      }
      
      headingIds.push({ text: heading.textContent, id: heading.id });
    });
    
    // 디버깅: 생성된 ID 확인
    console.log('Generated heading IDs:', headingIds);
    console.log('First 5 IDs:', headingIds.slice(0, 5));

    // 모든 앵커 링크에 클릭 핸들러 추가
    const links = content.querySelectorAll('a[href^="#"]');
    const clickHandlers = [];
    
    links.forEach((link) => {
      const handler = (e) => {
        e.preventDefault();
        const href = link.getAttribute('href') || '';
        let targetId = href.startsWith('#') ? href.substring(1) : href;
        
        // URL 디코딩
        targetId = decodeURIComponent(targetId);
        
        if (targetId) {
          console.log('Link clicked, target ID (decoded):', targetId);
          
          // 먼저 정확한 ID로 찾기
          let targetElement = document.getElementById(targetId);
          
          // 찾지 못한 경우, 모든 제목을 확인하여 텍스트 기반으로 찾기
          if (!targetElement) {
            const allHeadings = content.querySelectorAll('h1, h2, h3, h4, h5, h6');
            
            // ID에서 번호와 텍스트 분리 (예: "8-설치-절차" -> number: "8", text: "설치-절차")
            const idMatch = targetId.match(/^(\d+)-(.+)$/);
            
            if (idMatch) {
              const [, number, text] = idMatch;
              const searchText = text.replace(/-/g, ' '); // 하이픈을 공백으로 변환
              
              // 제목 텍스트에서 번호와 일치하는 것 찾기
              const matchingHeading = Array.from(allHeadings).find(heading => {
                const headingText = heading.textContent || '';
                const headingMatch = headingText.match(/^(\d+)\.\s*(.+)$/);
                
                if (headingMatch && headingMatch[1] === number) {
                  const headingTitle = headingMatch[2].trim();
                  // 텍스트가 유사한지 확인 (공백/하이픈 무시)
                  const normalizedHeading = headingTitle.replace(/[\s-]/g, '').toLowerCase();
                  const normalizedSearch = searchText.replace(/[\s-]/g, '').toLowerCase();
                  
                  return normalizedHeading === normalizedSearch || 
                         normalizedHeading.includes(normalizedSearch) ||
                         normalizedSearch.includes(normalizedHeading);
                }
                return false;
              });
              
              if (matchingHeading) {
                targetElement = matchingHeading;
                console.log('Found heading by text match:', matchingHeading.textContent, 'ID:', matchingHeading.id);
              }
            }
          }
          
          if (targetElement) {
            console.log('Target element found:', targetElement);
            scrollToElement(targetElement.id);
            // URL 해시 업데이트
            window.history.pushState(null, '', `#${targetElement.id}`);
          } else {
            console.warn('Target element not found for ID:', targetId);
            // 모든 제목 ID 출력
            const allHeadings = content.querySelectorAll('h1, h2, h3, h4, h5, h6');
            const availableIds = Array.from(allHeadings).map(h => ({ text: h.textContent, id: h.id }));
            console.log('Available heading IDs (first 10):', availableIds.slice(0, 10));
          }
        }
      };
      link.addEventListener('click', handler);
      clickHandlers.push({ link, handler });
    });

    // 초기 로드 시 URL 해시가 있으면 해당 위치로 스크롤
    const hash = window.location.hash.substring(1);
    if (hash) {
      scrollToElement(hash);
    }

    // 해시 변경 이벤트 리스너 추가 (브라우저 뒤로/앞으로 버튼 등)
    const handleHashChange = () => {
      const newHash = window.location.hash.substring(1);
      if (newHash) {
        scrollToElement(newHash);
      }
    };
    window.addEventListener('hashchange', handleHashChange);

    // cleanup
    return () => {
      clickHandlers.forEach(({ link, handler }) => {
        link.removeEventListener('click', handler);
      });
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [markdownContent, loading]);

  if (loading) {
    return (
      <>
        <div className="page-title-container">
          <h1 className="page-title">매뉴얼 페이지</h1>
        </div>
        <div className="page-container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>매뉴얼을 불러오는 중입니다...</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className="page-title-container">
          <h1 className="page-title">매뉴얼 페이지</h1>
        </div>
        <div className="page-container">
          <div className="error-message">
            <p>{error}</p>
            <button className="retry-button" onClick={() => window.location.reload()}>
              다시 시도
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="page-title-container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
        <h1 className="page-title">매뉴얼 페이지</h1>
        <button
          onClick={handleDownloadPdf}
          disabled={downloading}
          style={{
            padding: '8px 16px',
            backgroundColor: downloading ? '#ccc' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: downloading ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            marginRight: '22px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          {downloading ? (
            <>
              <span>다운로드 중...</span>
            </>
          ) : (
            <>
              <span></span>
              <span>PDF 다운로드</span>
            </>
          )}
        </button>
      </div>

      <div className="page-container">
        <div 
          ref={contentRef}
          className="manual-content" 
          style={{ 
            padding: '20px',
            lineHeight: '1.6',
            maxWidth: '100%',
            height: 'calc(100vh - 200px)',
            overflowY: 'auto',
            overflowX: 'hidden'
          }}
        >
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              img: ({ node, ...props }) => (
                <img {...props} alt={props.alt || ''} />
              ),
              table: ({ node, ...props }) => (
                <div style={{ overflowX: 'auto' }}>
                  <table {...props} style={{ width: '100%', borderCollapse: 'collapse' }} />
                </div>
              ),
            }}
          >
            {markdownContent}
          </ReactMarkdown>
        </div>
      </div>
    </>
  );
}

export default Manual;

